import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { t } from '../lib/i18n';
import './ProfileManagePage.css';

// 프로필 승계 파일 생성
async function createTransferFile(user, profile) {
  // 1) 해당 프로필에 연결된 기록 ID 조회
  const { data: shares } = await supabase
    .from('record_shares')
    .select('record_id')
    .eq('profile_id', profile.id);

  const recordIds = (shares || []).map((s) => s.record_id);
  if (recordIds.length === 0) {
    alert('이 프로필에 연결된 기록이 없습니다.');
    return null;
  }

  // 2) 기록 전체 조회
  const { data: records } = await supabase
    .from('records')
    .select('*')
    .in('id', recordIds);

  if (!records || records.length === 0) {
    alert('기록을 불러올 수 없습니다.');
    return null;
  }

  // 3) 사용된 카테고리 조회
  const catNames = [...new Set(records.map((r) => r.category_name).filter(Boolean))];
  let categories = [];
  if (catNames.length > 0) {
    const { data: cats } = await supabase
      .from('categories')
      .select('name, icon')
      .eq('user_id', user.id)
      .in('name', catNames);
    categories = cats || [];
  }

  // 4) 통계 계산
  const dates = records.map((r) => r.start_date).filter(Boolean).sort();
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];
  const totalDays = firstDate
    ? Math.floor((new Date(lastDate) - new Date(firstDate)) / 86400000) + 1
    : 0;

  const senderName =
    user.user_metadata?.full_name ||
    user.user_metadata?.name ||
    user.email?.split('@')[0] ||
    '사용자';

  return {
    type: 'profile_transfer',
    version: 1,
    created_at: new Date().toISOString(),
    sender_name: senderName,
    profile: { name: profile.name, emoji: profile.emoji },
    stats: {
      record_count: records.length,
      first_date: firstDate,
      last_date: lastDate,
      total_days: totalDays,
    },
    records,
    categories,
  };
}

const PROFILE_EMOJIS = [
  // 아기 · 어린이
  '👶', '🧒', '👦', '👧',
  // 어른
  '🧑', '👨', '👩', '🧔',
  // 노인
  '👴', '👵', '🧓', '👤',
  // 가족
  '👪', '👨‍👩‍👧', '👨‍👩‍👦', '👨‍👩‍👧‍👦',
  // 강아지 · 고양이
  '🐶', '🐱', '🐰', '🐹',
  // 동물
  '🐻', '🐼', '🐨', '🐯',
  '🦊', '🦁', '🐸', '🐧',
  '🐺', '🦝', '🐮', '🐷',
];

// 추가/수정 공용 폼 컴포넌트
function ProfileForm({ title, emoji, name, onEmojiChange, onNameChange, onConfirm, onCancel, confirmLabel, saving }) {
  return (
    <div className="profile-add-form">
      <p className="profile-add-form-title">{title}</p>

      <div className="profile-emoji-preview">
        <span>{emoji}</span>
      </div>
      <p className="profile-emoji-label">{t('record_emoji_select')}</p>
      <div className="profile-emoji-grid">
        {PROFILE_EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            className={`profile-emoji-btn ${emoji === e ? 'profile-emoji-btn--active' : ''}`}
            onClick={() => onEmojiChange(e)}
          >
            {e}
          </button>
        ))}
      </div>

      <input
        className="profile-name-input"
        type="text"
        placeholder={t('profile_name_placeholder')}
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onConfirm()}
        autoFocus
      />

      <div className="profile-add-actions">
        <button className="btn-cancel" onClick={onCancel}>{t('profile_cancel')}</button>
        <button className="btn-confirm" onClick={onConfirm} disabled={saving}>
          {saving ? t('record_saving') : confirmLabel}
        </button>
      </div>
    </div>
  );
}

export default function ProfileManagePage({
  user,
  profiles,
  currentProfile,
  onSelectProfile,
  onBack,
  onRefresh,
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmoji, setNewEmoji] = useState('👤');

  const [editingProfile, setEditingProfile] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmoji, setEditEmoji] = useState('👤');

  const [saving, setSaving] = useState(false);
  const [transferLoading, setTransferLoading] = useState(null); // profile.id or null

  const openAddForm = () => {
    setEditingProfile(null);
    setNewName('');
    setNewEmoji('👤');
    setShowAddForm(true);
  };

  const openEditForm = (profile) => {
    setShowAddForm(false);
    setEditName(profile.name);
    setEditEmoji(profile.emoji);
    setEditingProfile(profile);
  };

  const closeForm = () => {
    setShowAddForm(false);
    setEditingProfile(null);
  };

  const handleSelect = (profile) => {
    onSelectProfile(profile);
    onBack();
  };

  const handleAdd = async () => {
    if (!newName.trim()) { alert('프로필 이름을 입력해주세요.'); return; }
    setSaving(true);
    const { error } = await supabase.from('profiles').insert({
      user_id: user.id,
      name: newName.trim(),
      emoji: newEmoji,
    });
    setSaving(false);
    if (error) {
      alert('추가 실패: ' + error.message);
    } else {
      closeForm();
      onRefresh();
    }
  };

  const handleEditSave = async () => {
    if (!editName.trim()) { alert('프로필 이름을 입력해주세요.'); return; }
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ name: editName.trim(), emoji: editEmoji })
      .eq('id', editingProfile.id);
    setSaving(false);
    if (error) {
      alert('수정 실패: ' + error.message);
    } else {
      // 현재 선택된 프로필이 수정된 경우 상태도 갱신
      if (currentProfile?.id === editingProfile.id) {
        onSelectProfile({ ...editingProfile, name: editName.trim(), emoji: editEmoji });
      }
      closeForm();
      onRefresh();
    }
  };

  const handleTransfer = async (profile) => {
    if (!window.confirm(
      `"${profile.name}" 프로필의 기록을 승계 파일로 만드시겠습니까?\n\n파일을 받는 사람의 계정에서 불러올 수 있습니다.`
    )) return;

    setTransferLoading(profile.id);
    try {
      const transferData = await createTransferFile(user, profile);
      if (!transferData) { setTransferLoading(null); return; }

      const { stats } = transferData;
      const blob = new Blob([JSON.stringify(transferData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${profile.name}-승계-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert(
        `승계 파일이 생성되었습니다!\n\n` +
        `${profile.emoji} ${profile.name}\n` +
        `${stats.total_days.toLocaleString()}일간의 기록 ${stats.record_count}개\n` +
        `${stats.first_date} ~ ${stats.last_date}\n\n` +
        `이 파일을 받는 사람에게 전달해주세요.`
      );
    } catch (e) {
      alert('승계 파일 생성 실패: ' + e.message);
    }
    setTransferLoading(null);
  };

  const handleDelete = async (profile) => {
    if (!window.confirm(`"${profile.name}" 프로필을 삭제하시겠습니까?\n이 프로필의 기록은 삭제되지 않습니다.`)) return;
    const { error } = await supabase.from('profiles').delete().eq('id', profile.id);
    if (error) {
      alert('삭제 실패: ' + error.message);
    } else {
      if (currentProfile?.id === profile.id) {
        const remaining = profiles.filter(p => p.id !== profile.id);
        if (remaining.length > 0) onSelectProfile(remaining[0]);
      }
      if (editingProfile?.id === profile.id) closeForm();
      onRefresh();
    }
  };

  return (
    <div className="profile-manage-page">
      <header className="profile-manage-header">
        <button className="header-back" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h2>{t('profile_manage')}</h2>
        <div className="header-spacer" />
      </header>

      <div className="profile-manage-body">

        {/* 프로필 목록 */}
        <div className="profile-section">
          <p className="profile-section-label">{t('profile_my')}</p>
          <div className="profile-list">
            {profiles.map((profile) => {
              const isActive = currentProfile?.id === profile.id;
              const isEditing = editingProfile?.id === profile.id;
              return (
                <div
                  key={profile.id}
                  className={`profile-item ${isActive ? 'profile-item--active' : ''} ${isEditing ? 'profile-item--editing' : ''}`}
                >
                  <span className="profile-item-emoji">{profile.emoji}</span>
                  <div className="profile-item-text">
                    <span className="profile-item-name">{profile.name}</span>
                    {isActive && <span className="profile-item-badge">{t('profile_in_use')}</span>}
                  </div>
                  <div className="profile-item-actions">
                    {!isActive && (
                      <button className="profile-item-select" onClick={() => handleSelect(profile)}>
                        {t('profile_select')}
                      </button>
                    )}
                    <button
                      className="profile-item-transfer"
                      onClick={() => handleTransfer(profile)}
                      disabled={transferLoading === profile.id}
                      title={t('profile_transfer')}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                        <polyline points="16 6 12 2 8 6" />
                        <line x1="12" y1="2" x2="12" y2="15" />
                      </svg>
                    </button>
                    <button
                      className={`profile-item-edit ${isEditing ? 'profile-item-edit--active' : ''}`}
                      onClick={() => isEditing ? closeForm() : openEditForm(profile)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    {profiles.length > 1 && (
                      <button className="profile-item-delete" onClick={() => handleDelete(profile)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14H6L5 6" />
                          <path d="M10 11v6M14 11v6" />
                          <path d="M9 6V4h6v2" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 수정 / 추가 폼 */}
        {editingProfile ? (
          <ProfileForm
            title={`"${editingProfile.name}" ${t('profile_edit')}`}
            emoji={editEmoji}
            name={editName}
            onEmojiChange={setEditEmoji}
            onNameChange={setEditName}
            onConfirm={handleEditSave}
            onCancel={closeForm}
            confirmLabel={t('profile_save')}
            saving={saving}
          />
        ) : showAddForm ? (
          <ProfileForm
            title={t('profile_new')}
            emoji={newEmoji}
            name={newName}
            onEmojiChange={setNewEmoji}
            onNameChange={setNewName}
            onConfirm={handleAdd}
            onCancel={closeForm}
            confirmLabel={t('profile_add_btn')}
            saving={saving}
          />
        ) : (
          <button className="profile-add-btn" onClick={openAddForm}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {t('profile_add')}
          </button>
        )}

        <div className="profile-manage-hint">
          <p>{t('profile_hint1')}</p>
          <p>{t('profile_hint2')}</p>
        </div>
      </div>
    </div>
  );
}
